import MessageSheet from '@/components/MessageSheet.tsx';
import { ConferenceNotFoundError } from '@/error/ConferenceNotFoundError.ts';
import { conferenceService } from '@/service/ConferenceService';
import { ConferencePageState, useUIStore } from '@/store/UIStore';
import { AxiosError } from 'axios';
import { JOIN_REQUEST_REJECTED_MESSAGE, JoinRequestRejectedException } from 'intermind';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ConferenceErrorBoundary = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const setConferencePageState = useUIStore((state) => state.setConferencePageState);

  const errorHandler = useCallback(
    (e: Error | AxiosError) => {
      if (e instanceof ConferenceNotFoundError || (e instanceof AxiosError && e.response?.status === 404)) {
        setErrorMessages([t('errors.meetingNotFound')]);
      } else if (e instanceof AxiosError && e?.response?.data?.message === 'Conference capacity exceeded') {
        setErrorMessages([t('errors.capacityExceeded')]);
        conferenceService.closeSession();
      } else if (e.message?.includes("Failed to execute 'setParameters' on 'RTCRtpSender'")) {
        console.warn('Unsupported scalability mode');
      } else if (
        e instanceof JoinRequestRejectedException ||
        e.message === JOIN_REQUEST_REJECTED_MESSAGE ||
        (e instanceof AxiosError && e?.response?.data?.message === JOIN_REQUEST_REJECTED_MESSAGE)
      ) {
        setConferencePageState(ConferencePageState.REJECTED);
      } else {
        setErrorMessages([
          t('errors.errorOccurred'),
          e instanceof AxiosError ? e.response?.data?.message || e.message || '' : e.message || '',
        ]);
        conferenceService.closeSession();
      }
    },
    [setConferencePageState, t]
  );

  useEffect(() => {
    const errorListener = (event: ErrorEvent | PromiseRejectionEvent) => {
      if (event instanceof ErrorEvent) {
        errorHandler(event.error);
      } else if (event instanceof PromiseRejectionEvent) {
        errorHandler(event.reason);
      }
    };

    window.addEventListener('error', errorListener);
    window.addEventListener('unhandledrejection', errorListener);

    return () => {
      window.removeEventListener('error', errorListener);
      window.removeEventListener('unhandledrejection', errorListener);
    };
  }, [errorHandler]);

  if (errorMessages.length > 0) {
    return <MessageSheet message={errorMessages} />;
  }

  return <>{children}</>;
};

export default ConferenceErrorBoundary;
